<template>
  <div>
    <h2>{{ title }}</h2>
    <b-row>
      <b-col sm>
        <b-form-group description="Filter decks by name" label="Filter">
          <b-form-input
            v-model="filter"
            type="search"
            @update="currentPage = 1"
            debounce="500"
          ></b-form-input>
        </b-form-group>
      </b-col>
      <b-col sm>
        <TagPicker
          :options="tags"
          v-model="selectedTags"
          label="Tags"
          description="Filter decks by tags"
        />
      </b-col>
    </b-row>
    <MatchupCount
      :officialDeckCount="officialDeckCount"
      :fanDeckCount="rows"
      v-slot="{ total1v1, total2v2 }"
    >
      <p>
        There are <strong>{{ rows.toLocaleString() }}</strong> fan decks with
        the current filters and
        <strong>{{ officialDeckCount.toLocaleString() }}</strong> official
        decks. That makes
        <strong>{{ total1v1.toLocaleString() }}</strong> possible 1-on-1
        match-ups and <strong>{{ total2v2.toLocaleString() }}</strong> possible
        2-on-2 match-ups.
      </p>
    </MatchupCount>
    <b-table
      ref="table"
      :items="getDeckData"
      :fields="fields"
      :per-page="perPage"
      :current-page="currentPage"
      :filter="{ filter, tags: selectedTags }"
      :sort-by="initialSortBy"
      :sort-desc="initialSortDesc"
      @sort-changed="sortChanged"
      responsive
      class="compact-table-sm"
      :key="`user-decklist-table${this.username}`"
    >
      <template v-slot:cell(deck_name)="deck">
        <router-link :to="{ name: 'EditDeck', params: { id: deck.item.id } }">
          {{ deck.value }}
        </router-link>
      </template>
      <template v-slot:cell(published)="deck">
        <span class="text-nowrap">
          <font-awesome-icon
            v-if="deck.value"
            title="Published"
            :icon="['far', 'globe']"
          />
          <font-awesome-icon
            v-else
            title="Private"
            :icon="['fas', 'user-secret']"
          />
          <small> {{ deck.value ? "Published" : "Private" }}</small>
        </span>
      </template>
      <template v-slot:cell(tags)="data">
        <template v-for="tag in data.value">
          <b-badge href="#" @click="addTag(tag.name)" :key="tag.slug">{{
            tag.name
          }}</b-badge>
          {{ " " }}
        </template>
      </template>
      <template v-slot:cell(first_published)="data">
        <template v-if="data.value">
          {{ data.value }}
        </template>
        <template v-else-if="data.item.deck_published">
          <em
            ><small class="text-muted"
              >Unknown<sup
                class="info-tooltip"
                tabindex="0"
                v-b-tooltip
                title="Published before publication date was recorded"
                >?</sup
              ></small
            ></em
          >
        </template>
        <template v-else>
          <span class="text-nowrap">
            <font-awesome-icon title="Private" :icon="['fas', 'user-secret']" />
            <small> Private</small>
          </span>
        </template>
      </template>

      <template v-slot:head(likes)>
        <font-awesome-icon title="Likes" :icon="['far', 'thumbs-up']" />
      </template>
    </b-table>
    <b-pagination
      v-model="currentPage"
      v-if="rows > perPage"
      :total-rows="rows"
      :per-page="perPage"
      first-number
      last-number
    ></b-pagination>
  </div>
</template>

<script type="text/javascript">
import api from "@/api";
import dateFormat from "@/mixins/dateFormat";
import MatchupCount from "@/components/MatchupCount.vue";
import TagPicker from "@/components/TagPicker.vue";

export default {
  name: "UserDeckList",
  metaInfo() {
    const title = this.username ? `${this.title} | UmDb` : undefined;
    return {
      title,
    };
  },
  components: { MatchupCount, TagPicker },
  mixins: [dateFormat],
  props: {
    username: null,
  },
  data() {
    return {
      filter: "",
      selectedTags: [],
      tags: [],
      rows: 0,
      currentPage: 1,
      perPage: 20,
      initialSortBy:
        this.$store.state.userPreferences.deckListSortBy ?? "updated",
      initialSortDesc:
        this.$store.state.userPreferences.deckListSortDesc ?? true,
    };
  },
  computed: {
    officialDeckCount() {
      return this.$store.state.umDbDeckList.filter(
        (deck) => deck.cards.length > 0
      ).length;
    },
    isCurrentUser() {
      return this.username === this.$store.getters.currentUser;
    },
    title() {
      if (this.isCurrentUser) {
        return "My decks";
      }
      return `${this.username}'s decks`;
    },
    fields() {
      const fields = [
        {
          key: "deck_name",
          sortable: true,
        },
        {
          key: "tags",
          sortable: false,
        },
        {
          key: "likes",
          sortable: true,
          sortDirection: "desc",
        },
        this.isCurrentUser
          ? {
              key: "published",
              label: "Current version",
              sortable: true,
            }
          : "",
        {
          key: "first_published",
          sortable: true,
          sortDirection: "desc",
        },
        {
          key: "updated",
          sortable: true,
          sortDirection: "desc",
        },
      ];
      return fields;
    },
  },
  methods: {
    addTag(tag) {
      this.selectedTags.push(tag);
    },
    getDeckData(ctx, callback) {
      const {
        sortBy,
        sortDesc,
        perPage,
        currentPage,
        filter: { filter, tags },
      } = ctx;
      api
        .getUserDecks(this.username, {
          sortBy,
          sortDesc,
          perPage,
          currentPage,
          filter,
          tags,
        })
        .then((data) => {
          this.rows = data.total;
          const items = data.decks.map((deck) => {
            return {
              deck_name: deck.name,
              id: deck.id,
              created: this.formatDate(deck.created_on, "MMM D, YYYY"),
              updated: this.formatDate(deck.updated_on, "MMM D, YYYY"),
              first_published: this.formatDate(
                deck.deck_first_published_on,
                "MMM D, YYYY"
              ),
              creator: deck.user,
              tags: deck.tags,
              likes: deck.likes,
              published: deck.published,
              deck_published: deck.deck_published,
            };
          });
          callback(items);
        })
        .catch(() => {
          callback([]);
        });

      return null;
    },
    sortChanged({ sortBy, sortDesc }) {
      this.$store.commit("setPreference", {
        key: "deckListSortBy",
        value: sortBy,
      });
      this.$store.commit("setPreference", {
        key: "deckListSortDesc",
        value: sortDesc,
      });
    },
  },
  mounted() {
    api.getTags().then((tags) => {
      this.tags = tags;
    });
  },
};
</script>
